import React, { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  Box,
  Button,
  Paper,
  Typography,
  Alert,
  Grid,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Stack
} from '@mui/material';
import api from '../api';
import { useSchool } from '../contexts/SchoolContext';
import Spinner from '../components/Spinner';

interface AttendanceSummary {
  presentDays: number;
  lateDays: number;
  absentDays: number;
  excusedDays: number;
  totalDays: number;
}

interface TermSummary {
  quizScoreObtained: number;
  quizScoreTotal: number;
  assignmentScoreObtained: number;
  assignmentScoreTotal: number;
  testScoreObtained: number;
  testScoreTotal: number;
  examScoreObtained: number;
  examScoreTotal: number;
}

interface TermSubmissionRecord {
  _id: string;
  termName: string;
  className: string;
  staff: { name: string; email?: string };
  quizScoreObtained: number;
  quizScoreTotal: number;
  assignmentScoreObtained: number;
  assignmentScoreTotal: number;
  testScoreObtained?: number;
  testScoreTotal?: number;
  examScoreObtained?: number;
  examScoreTotal?: number;
  attendancePercentage?: number;
  isEligibleForExam?: boolean;
}

interface AttendanceRecord {
  _id: string;
  attendanceDate: string;
  status: string;
  remarks?: string;
  staff?: { name: string };
}

interface SubjectSummary {
  subject: string;
  quizScoreObtained: number;
  quizScoreTotal: number;
  assignmentScoreObtained: number;
  assignmentScoreTotal: number;
  testScoreObtained?: number;
  testScoreTotal?: number;
  examScoreObtained?: number;
  examScoreTotal?: number;
}

interface StudentTermDetails {
  student: {
    _id: string;
    name: string;
    email?: string;
    className?: string;
  };
  termName: string;
  termDurationMonths?: number;
  termStartDate?: string;
  termEndDate?: string;
  marksSubmissionDeadline?: string;
  attendancePercentage: number;
  isEligibleForExam: boolean;
  attendanceSummary: AttendanceSummary;
  termSummary: TermSummary;
  submissions: TermSubmissionRecord[];
  subjectSummaries: SubjectSummary[];
  attendanceRecords: AttendanceRecord[];
}

const SchoolTermResultStudent: React.FC = () => {
  const { school, loading } = useSchool();
  const { studentId } = useParams();
  const navigate = useNavigate();
  const [loadingDetails, setLoadingDetails] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [details, setDetails] = useState<StudentTermDetails | null>(null);

  useEffect(() => {
    const fetchDetails = async () => {
      if (!school?._id || !studentId) return;

      setLoadingDetails(true);
      setError(null);

      try {
        const response = await api.get(`/schools/${school._id}/term-submissions/student/${studentId}`);
        setDetails(response.data as StudentTermDetails);
      } catch (err: any) {
        setError(err.response?.data?.message || 'Unable to load student term details');
      } finally {
        setLoadingDetails(false);
      }
    };

    fetchDetails();
  }, [school?._id, studentId]);

  if (loading || loadingDetails) {
    return (
      <Box display="flex" justifyContent="center" mt={4}>
        <Spinner />
      </Box>
    );
  }

  const studentName = details?.student?.name || 'Student';
  const className = details?.student?.className || 'N/A';
  const termName = details?.termName || 'Active Term';

  return (
    <Box>
      <Button variant="outlined" sx={{ mb: 2 }} onClick={() => navigate('/school/term-results')}>
        Back to Term Results
      </Button>

      <Typography variant="h4" sx={{ mb: 3, fontWeight: 600 }}>
        {studentName} - Term Details
      </Typography>

      {error ? (
        <Alert severity="error">{error}</Alert>
      ) : details ? (
        <>
          <Paper sx={{ p: 3, mb: 4 }}>
            <Grid container spacing={3}>
              <Grid item xs={12} sm={6} md={3}>
                <Typography variant="subtitle2" color="text.secondary">Student</Typography>
                <Typography variant="h6" sx={{ mt: 1 }}>{studentName}</Typography>
                <Typography color="text.secondary" sx={{ mt: 0.5 }}>{details.student.email || 'No email available'}</Typography>
              </Grid>
              <Grid item xs={12} sm={6} md={3}>
                <Typography variant="subtitle2" color="text.secondary">Class</Typography>
                <Typography variant="h6" sx={{ mt: 1 }}>{className}</Typography>
              </Grid>
              <Grid item xs={12} sm={6} md={3}>
                <Typography variant="subtitle2" color="text.secondary">Term</Typography>
                <Typography variant="h6" sx={{ mt: 1 }}>{termName}</Typography>
              </Grid>
              <Grid item xs={12} sm={6} md={3}>
                <Typography variant="subtitle2" color="text.secondary">Duration</Typography>
                <Typography variant="h6" sx={{ mt: 1 }}>
                  {details.termDurationMonths ? `${details.termDurationMonths} month${details.termDurationMonths === 1 ? '' : 's'}` : 'Not configured'}
                </Typography>
              </Grid>
              {details.termEndDate && (
                <Grid item xs={12} sm={6} md={3}>
                  <Typography variant="subtitle2" color="text.secondary">Term Ends</Typography>
                  <Typography variant="h6" sx={{ mt: 1 }}>
                    {new Date(details.termEndDate).toLocaleDateString()}
                  </Typography>
                </Grid>
              )}
              <Grid item xs={12} sm={6} md={3}>
                <Typography variant="subtitle2" color="text.secondary">Eligibility</Typography>
                <Typography variant="h6" sx={{ mt: 1, color: details.isEligibleForExam ? 'success.main' : 'error.main' }}>
                  {details.isEligibleForExam ? 'Eligible' : 'Not eligible'}
                </Typography>
              </Grid>
            </Grid>
          </Paper>

          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} mb={4}>
            <Paper sx={{ p: 3, flex: 1 }}>
              <Typography variant="subtitle2" color="text.secondary">Attendance Percentage</Typography>
              <Typography variant="h5" sx={{ mt: 1, fontWeight: 700 }}>
                {Number(details.attendancePercentage ?? 0).toFixed(2)}%
              </Typography>
            </Paper>
          </Stack>

          <Paper sx={{ p: 3, mb: 4 }}>
            <Typography variant="h6" sx={{ mb: 2 }}>Attendance Summary</Typography>
            <Grid container spacing={2}>
              <Grid item xs={6} sm={3}>
                <Typography variant="subtitle2" color="text.secondary">Present</Typography>
                <Typography variant="h6">{details.attendanceSummary.presentDays}</Typography>
              </Grid>
              <Grid item xs={6} sm={3}>
                <Typography variant="subtitle2" color="text.secondary">Late</Typography>
                <Typography variant="h6">{details.attendanceSummary.lateDays}</Typography>
              </Grid>
              <Grid item xs={6} sm={3}>
                <Typography variant="subtitle2" color="text.secondary">Absent</Typography>
                <Typography variant="h6">{details.attendanceSummary.absentDays}</Typography>
              </Grid>
              <Grid item xs={6} sm={3}>
                <Typography variant="subtitle2" color="text.secondary">Excused</Typography>
                <Typography variant="h6">{details.attendanceSummary.excusedDays}</Typography>
              </Grid>
            </Grid>
          </Paper>

          <Paper sx={{ p: 3, mb: 4 }}>
            <Typography variant="h6" sx={{ mb: 2 }}>Submitted Term Records</Typography>
            {details.subjectSummaries?.length > 0 ? (
              <TableContainer sx={{ overflowX: 'auto', width: '100%' }}>
                <Table sx={{ minWidth: 1120, tableLayout: 'fixed' }}>
                  <TableHead>
                    <TableRow>
                      <TableCell sx={{ width: '20%', whiteSpace: 'nowrap', fontWeight: 600 }}>Subject</TableCell>
                      <TableCell sx={{ width: '20%', whiteSpace: 'nowrap', fontWeight: 600 }}>Quiz Score</TableCell>
                      <TableCell sx={{ width: '20%', whiteSpace: 'nowrap', fontWeight: 600 }}>Assignment Score</TableCell>
                      <TableCell sx={{ width: '20%', whiteSpace: 'nowrap', fontWeight: 600 }}>Test Score</TableCell>
                      <TableCell sx={{ width: '20%', whiteSpace: 'nowrap', fontWeight: 600 }}>Exam Score</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {details.subjectSummaries.map((subject) => (
                      <TableRow key={subject.subject}>
                        <TableCell sx={{ whiteSpace: 'nowrap' }}>{subject.subject}</TableCell>
                        <TableCell sx={{ whiteSpace: 'nowrap' }}>{subject.quizScoreObtained || 0}</TableCell>
                        <TableCell sx={{ whiteSpace: 'nowrap' }}>{subject.assignmentScoreObtained || 0}</TableCell>
                        <TableCell sx={{ whiteSpace: 'nowrap' }}>{subject.testScoreObtained || 0}</TableCell>
                        <TableCell sx={{ whiteSpace: 'nowrap' }}>{subject.examScoreObtained || 0}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            ) : null}

            {details.subjectSummaries?.length === 0 && details.submissions.length === 0 && (
              <Alert severity="info">No term submissions were found for this student.</Alert>
            )}

            {details.submissions.length > 0 && (
              <Box sx={{ mt: details.subjectSummaries?.length > 0 ? 3 : 0 }}>
                {details.subjectSummaries?.length > 0 && (
                  <Typography variant="subtitle1" sx={{ mb: 2 }}>
                    Teacher-entered submission details
                  </Typography>
                )}
                <TableContainer sx={{ overflowX: 'auto' }}>
                  <Table sx={{ minWidth: 900 }}>
                    <TableHead>
                      <TableRow>
                        <TableCell>Teacher</TableCell>
                        <TableCell>Class</TableCell>
                        <TableCell>Quiz Score</TableCell>
                        <TableCell>Assignment Score</TableCell>
                        <TableCell>Test Score</TableCell>
                        <TableCell>Exam Score</TableCell>
                        <TableCell>Attendance %</TableCell>
                        <TableCell>Eligibility</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {details.submissions.map((record) => (
                        <TableRow key={record._id}>
                          <TableCell>{record.staff?.name || 'Unknown'}</TableCell>
                          <TableCell>{record.className || className}</TableCell>
                          <TableCell>{record.quizScoreObtained || 0}</TableCell>
                          <TableCell>{record.assignmentScoreObtained || 0}</TableCell>
                          <TableCell>{record.testScoreObtained || 0}</TableCell>
                          <TableCell>{record.examScoreObtained || 0}</TableCell>
                          <TableCell>{Number(record.attendancePercentage ?? 0).toFixed(2)}%</TableCell>
                          <TableCell>{record.isEligibleForExam ? 'Yes' : 'No'}</TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              </Box>
            )}
          </Paper>

          <Paper sx={{ p: 3, mb: 4 }}>
            <Typography variant="h6" sx={{ mb: 2 }}>Recent Attendance Records</Typography>
            {details.attendanceRecords.length === 0 ? (
              <Alert severity="info">No attendance records were found for this student.</Alert>
            ) : (
              <TableContainer>
                <Table>
                  <TableHead>
                    <TableRow>
                      <TableCell>Date</TableCell>
                      <TableCell>Status</TableCell>
                      <TableCell>Recorded By</TableCell>
                      <TableCell>Remarks</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {details.attendanceRecords.map((record) => (
                      <TableRow key={record._id}>
                        <TableCell>{new Date(record.attendanceDate).toLocaleDateString()}</TableCell>
                        <TableCell>{record.status}</TableCell>
                        <TableCell>{record.staff?.name || 'Unknown'}</TableCell>
                        <TableCell>{record.remarks || '—'}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            )}
          </Paper>
        </>
      ) : (
        <Alert severity="info">No details available for this student.</Alert>
      )}
    </Box>
  );
};

export default SchoolTermResultStudent;
